import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ClickTickEvent, Player } from '@clicklinx/protocol'
import { SEAT_COLORS } from '../lib/seats'
import { formatClock } from '../lib/format'
import { useCountdown } from '../lib/useCountdown'

/** Clicks are shipped in batches rather than one message per click. */
const BATCH_INTERVAL_MS = 250
/** Bounded so a very fast burst cannot pile up unbounded DOM nodes. */
const MAX_RIPPLES = 14

interface Ripple {
  id: number
  x: number
  y: number
}

export function ClickRaceStage({
  startedAtLocal,
  durationMs,
  roundKey,
  players,
  tick,
  myUserId,
  onClicks,
}: {
  startedAtLocal: number
  durationMs: number
  /** Stable identity for this round: the server's own start timestamp. */
  roundKey: number
  players: Player[]
  tick: ClickTickEvent | null
  myUserId: string
  onClicks: (offsets: number[]) => void
}) {
  const reduceMotion = useReducedMotion()

  /**
   * The timing baseline is captured once per round and then held.
   *
   * `startedAtLocal` is derived from the clock offset, which the socket
   * refines every few seconds. Letting that flow straight through meant the
   * value changed mid-race, which reset the counter to zero and discarded
   * buffered clicks - the score visibly went backwards while the player was
   * doing nothing wrong. Sub-millisecond drift is not worth that.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const raceStart = useMemo(() => startedAtLocal, [roundKey])

  const remaining = useCountdown(raceStart + durationMs)

  // The local count updates on the same frame as the press. Waiting for the
  // server tick would put ~100ms of lag on the one number the player is
  // staring at, and the race would feel broken even though it is not.
  const [localCount, setLocalCount] = useState(0)
  const [ripples, setRipples] = useState<Ripple[]>([])

  const bufferRef = useRef<number[]>([])
  const rippleIdRef = useRef(0)
  const finishedRef = useRef(false)

  // Keyed on the round, not on the derived local timestamp, so a clock
  // refinement mid-race cannot wipe the count or drop buffered clicks.
  useEffect(() => {
    setLocalCount(0)
    bufferRef.current = []
    finishedRef.current = false
  }, [roundKey])

  useEffect(() => {
    const flush = () => {
      if (bufferRef.current.length === 0) return
      onClicks(bufferRef.current.splice(0, bufferRef.current.length))
    }
    const timer = setInterval(flush, BATCH_INTERVAL_MS)
    return () => {
      // Ship whatever is left so the final clicks before the buzzer still count.
      flush()
      clearInterval(timer)
    }
  }, [onClicks])

  const handlePress = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      // Rounded independently of the clock offset: the wire format is integer
      // milliseconds, and a fractional value here is dropped by the server for
      // the whole match rather than for one click.
      const offset = Math.round(Date.now() - raceStart)
      if (offset < 0 || offset > durationMs || finishedRef.current) return

      bufferRef.current.push(offset)
      setLocalCount((count) => count + 1)

      if (!reduceMotion) {
        const rect = event.currentTarget.getBoundingClientRect()
        const id = rippleIdRef.current++
        setRipples((current) => [
          ...current.slice(-(MAX_RIPPLES - 1)),
          {
            id,
            x: ((event.clientX - rect.left) / rect.width) * 100,
            y: ((event.clientY - rect.top) / rect.height) * 100,
          },
        ])
        window.setTimeout(() => {
          setRipples((current) => current.filter((r) => r.id !== id))
        }, 520)
      }
    },
    [raceStart, durationMs, reduceMotion],
  )

  if (remaining <= 0) finishedRef.current = true

  const serverScores = new Map(tick?.scores.map((s) => [s.userId, s.score]) ?? [])
  const bestScore = Math.max(
    localCount,
    ...players.map((p) => serverScores.get(p.userId) ?? 0),
    1,
  )
  const urgent = remaining <= 5_000

  return (
    <div className="flex min-h-0 w-full flex-1 flex-col gap-4">
      {/* Live race bars. This is the "who is winning" read - the numbers alone
          do not convey a gap at a glance. */}
      <div className="space-y-2">
        {players.map((player) => {
          const score = player.userId === myUserId ? localCount : (serverScores.get(player.userId) ?? 0)
          const isMe = player.userId === myUserId
          return (
            <div key={player.userId} className="flex items-center gap-3">
              <span
                className="w-20 shrink-0 truncate text-xs font-semibold sm:w-28"
                style={{ color: SEAT_COLORS[player.color] }}
              >
                {isMe ? 'You' : player.displayName}
              </span>
              <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-ink-800">
                <motion.div
                  className="h-full rounded-full"
                  style={{ background: SEAT_COLORS[player.color] }}
                  animate={{ width: `${(score / bestScore) * 100}%` }}
                  transition={{ type: 'spring', stiffness: 260, damping: 34 }}
                />
              </div>
              <span
                className="w-12 shrink-0 text-right font-mono text-sm font-semibold tnum"
                style={{ color: SEAT_COLORS[player.color] }}
              >
                {score}
              </span>
            </div>
          )
        })}
      </div>

      {/* The pad */}
      <div className="flex min-h-0 flex-1 items-center justify-center">
        <motion.button
          type="button"
          onPointerDown={handlePress}
          aria-label="Click as fast as you can"
          whileTap={reduceMotion ? undefined : { scale: 0.985 }}
          transition={{ type: 'spring', stiffness: 900, damping: 40 }}
          className="no-tap-delay relative aspect-square w-full max-w-[min(100%,26rem)] overflow-hidden rounded-full border border-lime/25 focus-visible:outline-none"
          style={{
            maxHeight: '100%',
            background:
              'radial-gradient(circle at 50% 42%, rgba(215,255,62,0.16), rgba(215,255,62,0.04) 46%, transparent 70%), #0e1013',
            boxShadow: 'inset 0 0 60px rgba(215,255,62,0.09), 0 30px 80px -50px rgba(215,255,62,0.6)',
          }}
        >
          {/* Concentric rings give the pad depth and make the centre obvious. */}
          <span aria-hidden className="pointer-events-none absolute inset-0">
            {[0.42, 0.62, 0.82].map((scale) => (
              <span
                key={scale}
                className="absolute left-1/2 top-1/2 rounded-full border border-lime/10"
                style={{
                  width: `${scale * 100}%`,
                  height: `${scale * 100}%`,
                  transform: 'translate(-50%, -50%)',
                }}
              />
            ))}
          </span>

          <AnimatePresence>
            {ripples.map((ripple) => (
              <motion.span
                key={ripple.id}
                aria-hidden
                className="pointer-events-none absolute rounded-full border border-lime/60"
                style={{ left: `${ripple.x}%`, top: `${ripple.y}%` }}
                initial={{ width: 0, height: 0, opacity: 0.85, x: '-50%', y: '-50%' }}
                animate={{ width: 150, height: 150, opacity: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              />
            ))}
          </AnimatePresence>

          <span className="pointer-events-none relative flex h-full w-full flex-col items-center justify-center">
            <span className="font-mono text-[clamp(3.5rem,16vw,6.5rem)] font-bold leading-none tnum text-lime text-glow">
              {localCount}
            </span>
            <span className="eyebrow mt-3">clicks</span>
          </span>
        </motion.button>
      </div>

      {/* Clock */}
      <div className="flex items-center justify-center">
        <motion.span
          className="font-mono text-3xl font-semibold tnum sm:text-4xl"
          animate={
            urgent && !reduceMotion
              ? { color: ['#ff4d3d', '#ffffff', '#ff4d3d'] }
              : { color: '#f2f4f7' }
          }
          transition={urgent ? { duration: 0.9, repeat: Infinity } : { duration: 0.2 }}
        >
          {formatClock(remaining)}
        </motion.span>
      </div>
    </div>
  )
}
